/**
 * useOutbox — FWA-PWA-029.
 *
 * Reactive snapshot del outbox vía TanStack Query con custom queryFn que lee IDB.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  clearAll as clearAllDb,
  countByStatus,
  deleteOutboxItem,
  listOutbox,
} from '../outbox/db.js';
import { syncOutbox } from '../outbox/sync.js';
import type { OutboxRecord, OutboxStatus } from '../outbox/types.js';

export const outboxKeys = {
  all: ['outbox'] as const,
  list: ['outbox', 'list'] as const,
  counts: ['outbox', 'counts'] as const,
};

export function useOutboxList() {
  return useQuery<OutboxRecord[]>({
    queryKey: outboxKeys.list,
    queryFn: () => listOutbox(),
    staleTime: 1_000,
  });
}

export function useOutboxCounts() {
  return useQuery<Record<OutboxStatus, number>>({
    queryKey: outboxKeys.counts,
    queryFn: () => countByStatus(),
    staleTime: 1_000,
  });
}

export function useSyncOutbox(onAuthInvalidated?: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['outbox', 'sync'],
    mutationFn: () => syncOutbox(onAuthInvalidated ? { onAuthInvalidated } : {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: outboxKeys.all });
      // Mutations recientemente sincronizadas → refresca caches de entidades
      void qc.invalidateQueries({ queryKey: ['ninas'] });
      void qc.invalidateQueries({ queryKey: ['adelantos'] });
    },
  });
}

export function useDeleteOutboxItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['outbox', 'delete'],
    mutationFn: (id: string) => deleteOutboxItem(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: outboxKeys.all });
    },
  });
}

export function useClearOutbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['outbox', 'clear'],
    mutationFn: () => clearAllDb(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: outboxKeys.all });
    },
  });
}
